import HandleResponses from '@common/utils/handleResponses';
import axios from '../../../tms-indent-axios';

const bulkPodActions = {
  /**
   * Initialize empty bulk upload transaction (Step 0)
   */
  async initializeBulkUpload({ commit }) {
    try {
      const result = await axios.post('/order/pod_bulk_upload_init');
      commit('SET_POD_BULK_UPLOAD_ID', result.bulk_upload_id);
      commit('SET_POD_BULK_UPLOAD_DATA', result);
      return result;
    } catch (error) {
      return HandleResponses.Status(error);
    }
  },

  /**
   * Upload single POD file with validation (Step 1)
   */
  async uploadSingleFile({ commit }, { bulkUploadId, file }) {
    try {
      const formData = new FormData();
      formData.append('bulk_upload_id', bulkUploadId);
      formData.append('file', file);

      const result = await axios.post('/order/pod_file_upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return result;
    } catch (error) {
      // Return actual error from API instead of generic message
      if (error && error.data && error.data.errors && error.data.errors.length > 0) {
        return {
          success: false,
          reason: error.data.errors[0].message || error.data.errors[0].field,
          errors: error.data.errors,
        };
      }
      return HandleResponses.Status(error);
    }
  },

  /**
   * List uploaded files (Step 2)
   */
  async listFiles({ commit }, bulkUploadId) {
    try {
      const result = await axios.get(`/order/pod_files_list/${bulkUploadId}`);
      return result;
    } catch (error) {
      return HandleResponses.Status(error);
    }
  },

  /**
   * Delete single file (Step 3)
   */
  async deleteFile({ commit }, { bulkUploadId, fileName }) {
    try {
      const result = await axios.delete('/order/pod_file_delete', {
        data: {
          bulk_upload_id: bulkUploadId,
          file_name: fileName,
        },
      });
      return result;
    } catch (error) {
      return HandleResponses.Status(error);
    }
  },

  /**
   * Check if user has an active bulk upload transaction
   */
  async checkActiveTransaction({ commit }) {
    try {
      const result = await axios.get('/order/pod_bulk_upload/active');
      if (result.has_active) {
        commit('SET_ACTIVE_POD_BULK_UPLOAD', result);
      } else {
        commit('CLEAR_ACTIVE_POD_BULK_UPLOAD');
      }
      return result;
    } catch (error) {
      commit('CLEAR_ACTIVE_POD_BULK_UPLOAD');
      return HandleResponses.Status(error);
    }
  },

  /**
   * Upload multiple POD files (Step 1)
   */
  async uploadPodFiles({ commit }, files) {
    try {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('files', file);
      });

      const result = await axios.post('/order/pod_files_upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      commit('SET_POD_BULK_UPLOAD_ID', result.bulk_upload_id);
      commit('SET_POD_BULK_UPLOAD_DATA', result);
      return result;
    } catch (error) {
      return HandleResponses.Status(error);
    }
  },

  /**
   * Download Excel template
   */
  async downloadTemplate(_, bulkUploadId) {
    try {
      const result = await axios.get(`/order/pod_bulk_template/${bulkUploadId}`, {
        responseType: 'blob',
      });

      // Trigger download
      const url = window.URL.createObjectURL(new Blob([result]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `pod_mapping_template_${bulkUploadId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      return { success: true };
    } catch (error) {
      // For blob responses, errors come as blob too
      if (error && error.data instanceof Blob) {
        const text = await error.data.text();
        try {
          const errorJson = JSON.parse(text);
          return {
            success: false,
            error: errorJson.errors ? errorJson.errors[0].message : 'Failed to download template',
          };
        } catch (e) {
          return { success: false, error: 'Failed to download template' };
        }
      }
      return HandleResponses.Status(error);
    }
  },

  /**
   * Upload mapping Excel (Step 2)
   */
  async uploadMappingExcel({ commit }, { bulkUploadId, file }) {
    try {
      const formData = new FormData();
      formData.append('bulk_upload_id', bulkUploadId);
      formData.append('mapping_file', file);

      const result = await axios.post('/order/pod_bulk_upload_mapping', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      commit('SET_POD_BULK_STATUS', 'processing');
      return result;
    } catch (error) {
      return HandleResponses.Status(error);
    }
  },

  /**
   * Get upload status (polling)
   */
  async fetchBulkUploadStatus({ commit }, bulkUploadId) {
    try {
      const result = await axios.get(`/order/pod_bulk_upload_status/${bulkUploadId}`);
      commit('SET_POD_BULK_STATUS_DATA', result);
      return result;
    } catch (error) {
      return HandleResponses.Status(error);
    }
  },

  /**
   * Download upload rejections (format/size errors)
   */
  async downloadUploadRejections(_, bulkUploadId) {
    try {
      const result = await axios.get(`/order/pod_bulk_upload/upload_rejections/${bulkUploadId}`, {
        responseType: 'blob',
      });

      // Trigger download
      const url = window.URL.createObjectURL(new Blob([result]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `upload_rejections_${bulkUploadId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      return { success: true };
    } catch (error) {
      return HandleResponses.Status(error);
    }
  },

  /**
   * Download processing rejections (LR validation errors)
   */
  async downloadProcessingRejections(_, bulkUploadId) {
    try {
      const result = await axios.get(`/order/pod_bulk_upload/processing_rejections/${bulkUploadId}`, {
        responseType: 'blob',
      });

      // Trigger download
      const url = window.URL.createObjectURL(new Blob([result]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `processing_rejections_${bulkUploadId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      return { success: true };
    } catch (error) {
      return HandleResponses.Status(error);
    }
  },

  /**
   * Discard active transaction
   */
  async discardTransaction({ commit }, bulkUploadId) {
    try {
      const result = await axios.post('/order/pod_bulk_upload/discard', {
        bulk_upload_id: bulkUploadId,
      });

      commit('CLEAR_POD_BULK_UPLOAD_STATE');
      return result;
    } catch (error) {
      return HandleResponses.Status(error);
    }
  },
};

export default bulkPodActions;
